/**
 * Ürünler page (Mağaza) over GET /shop/products: every product card with its thumbnail,
 * vehicle, type, coverage, formats, triangle count and price; a card opens the detail with the
 * gallery (renders and guide previews, full size on request), the facts, what each file covers
 * (the archive review's notes) and the Etsy listing. Read only. Sercan, 2026-09-30: "elimizdeki
 * tüm araçlar ve onların kartlarını fotoğraflarıyla görebiliyor muyum?".
 */

import { useEffect, useId, useState } from 'react';
import {
  cabinInteriorLabel,
  fileCoverageLabel,
  formatBytes,
  formatExtent,
  formatTriangles,
  imageKindLabel,
  type ProductCard,
  type ProductImage,
  productCoverageLabel,
  productImageUrl,
  productSearchText,
  productStatusLabel,
  productTypeLabel,
  productVehicleLabel,
} from '../api/products.js';
import { centsToMicro } from '../api/shop.js';
import { type ProductsState, useProducts } from '../app/products.js';
import { formatNumber, formatUsd } from '../lib/format.js';
import { EmptyState, Segmented } from '../screens/common.js';
import { Icon } from './icons.js';

type ProductFilter = 'aktif' | 'taslak' | 'tumu';

function useProductsView(): { state: ProductsState; reload: () => void } {
  const { store, state } = useProducts();
  useEffect(() => {
    void store.ensureLoaded();
  }, [store]);
  return { state, reload: () => void store.reload() };
}

function ProductsStatus({ state, reload }: { state: ProductsState; reload: () => void }) {
  if (state.error !== null && state.view === null) {
    return (
      <div className="shop-status" role="status">
        Ürün verisi yüklenemedi: {state.error}{' '}
        <button type="button" className="btn btn--secondary" onClick={reload}>
          Tekrar dene
        </button>
      </div>
    );
  }
  return (
    <div className="shop-status" role="status">
      Yükleniyor…
    </div>
  );
}

/** An image of the media route; when the bytes do not come (mock, missing file) the frame stays. */
function ProductImg({
  image,
  size,
  className,
  alt,
  fallback,
}: {
  image: ProductImage;
  size: 'thumb' | 'full';
  className: string;
  alt: string;
  fallback: string;
}) {
  const [failed, setFailed] = useState(false);
  const url = productImageUrl(image.assetId, size);
  useEffect(() => setFailed(false), []);
  if (failed) {
    return (
      <span className={`${className} product-img--missing`} aria-label={alt} role="img">
        <Icon name="image" size={20} />
        <span className="mono">{fallback}</span>
      </span>
    );
  }
  return (
    <img className={className} src={url} alt={alt} loading="lazy" decoding="async" onError={() => setFailed(true)} />
  );
}

function Placeholder({ product }: { product: ProductCard }) {
  return (
    <span className="product-card__placeholder">
      <Icon name={['scan', 'bundle', 'model'].includes(product.productType) ? 'package' : 'file-text'} size={22} />
      <span className="mono">{product.formats.join(' · ') || productTypeLabel(product.productType)}</span>
    </span>
  );
}

function cardMeta(product: ProductCard): string[] {
  const parts = [productTypeLabel(product.productType), productCoverageLabel(product.coverage)];
  if (product.formats.length > 0) parts.push(product.formats.join(', '));
  if (product.polyCount !== null) parts.push(`${formatTriangles(product.polyCount)} üçgen`);
  if (product.images.length > 0) parts.push(`${product.images.length} görsel`);
  return parts;
}

function ProductCardItem({ product, onOpen }: { product: ProductCard; onOpen: () => void }) {
  const cover = product.images[0] ?? null;
  const vehicle = productVehicleLabel(product.vehicle);
  return (
    <li className="products-grid__item">
      <button type="button" className={`product-card product-card--${product.status}`} onClick={onOpen}>
        <span className="product-card__media">
          {cover ? (
            <ProductImg
              image={cover}
              size="thumb"
              className="product-card__img"
              alt=""
              fallback={product.formats[0] ?? ''}
            />
          ) : (
            <Placeholder product={product} />
          )}
          {product.status !== 'active' ? (
            <span className="product-card__badge">{productStatusLabel(product.status)}</span>
          ) : null}
        </span>
        <span className="product-card__body">
          {vehicle ? <span className="product-card__vehicle">{vehicle}</span> : null}
          <span className="product-card__title">{product.title}</span>
          <span className="product-card__meta">
            {cardMeta(product).join(' · ')}
            {product.priceCents !== null ? (
              <>
                {' · '}
                <span className="mono">{formatUsd(centsToMicro(product.priceCents), 0)}</span>
              </>
            ) : null}
          </span>
        </span>
      </button>
    </li>
  );
}

function Facts({ product }: { product: ProductCard }) {
  const rows: { label: string; value: string }[] = [
    { label: 'Tür', value: productTypeLabel(product.productType) },
    { label: 'Kapsam', value: productCoverageLabel(product.coverage) },
    { label: 'Biçimler', value: product.formats.join(', ') || 'belirtilmemiş' },
  ];
  if (product.polyCount !== null) rows.push({ label: 'Üçgen', value: formatNumber(product.polyCount) });
  const extent = product.geometry?.bboxMm ?? product.files.find((file) => file.extentMm !== null)?.extentMm ?? null;
  if (extent !== null)
    rows.push({ label: 'Ölçü (U x G x Y)', value: formatExtent(extent, product.geometry?.unit ?? 'mm') });
  if (product.fileSizeMb !== null)
    rows.push({ label: 'Dosya boyutu', value: `${formatNumber(product.fileSizeMb, 0)} MB` });
  if (product.compatibleYearFrom !== null || product.compatibleYearTo !== null) {
    rows.push({
      label: 'Uyumlu yıllar',
      value: `${product.compatibleYearFrom ?? '…'}-${product.compatibleYearTo ?? '…'}`,
    });
  }
  if (product.priceCents !== null) rows.push({ label: 'Fiyat', value: formatUsd(centsToMicro(product.priceCents), 0) });
  rows.push({ label: 'Durum', value: productStatusLabel(product.status) });
  rows.push({ label: 'SKU', value: product.sku });
  return (
    <dl className="product-facts">
      {rows.map((row) => (
        <div key={row.label} className="product-facts__row">
          <dt>{row.label}</dt>
          <dd className={row.label === 'SKU' || row.label === 'Üçgen' ? 'mono' : undefined}>{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function Gallery({ product }: { product: ProductCard }) {
  const [index, setIndex] = useState(0);
  const images = product.images;
  const current = images[Math.min(index, images.length - 1)] ?? null;
  if (current === null) {
    return (
      <p className="shop-hint">
        Bu ürünün henüz görseli yok
        {product.productType === 'step' || product.productType === 'plan' ? ' (STEP ve plan dosyaları çizilmez)' : ''}.
      </p>
    );
  }
  return (
    <div className="product-gallery">
      <figure className="product-gallery__main">
        <ProductImg
          image={current}
          size="thumb"
          className="product-gallery__img"
          alt={current.title ?? product.title}
          fallback={imageKindLabel(current.kind)}
        />
        <figcaption className="product-gallery__caption">
          <span className="product-gallery__kind">{imageKindLabel(current.kind)}</span>
          {current.title ? <span> · {current.title}</span> : null}
          <a
            className="product-gallery__full"
            href={productImageUrl(current.assetId, 'full')}
            target="_blank"
            rel="noreferrer"
          >
            Tam boy aç
            <Icon name="external-link" size={12} />
          </a>
        </figcaption>
      </figure>
      {images.length > 1 ? (
        <ul className="product-gallery__thumbs" aria-label="Görseller">
          {images.map((image, i) => (
            <li key={image.assetId}>
              <button
                type="button"
                className={i === index ? 'product-thumb product-thumb--on' : 'product-thumb'}
                aria-label={`Görsel ${i + 1}: ${image.title ?? imageKindLabel(image.kind)}`}
                aria-pressed={i === index}
                onClick={() => setIndex(i)}
              >
                <ProductImg image={image} size="thumb" className="product-thumb__img" alt="" fallback={String(i + 1)} />
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function Files({ product }: { product: ProductCard }) {
  if (product.files.length === 0 && product.coverageNote === null && product.contents === null) return null;
  return (
    <section className="product-files" aria-labelledby={`urun-dosyalar-${product.productId}`}>
      <h3 id={`urun-dosyalar-${product.productId}`} className="product-detail__h3">
        Kapsam ve dosyalar
      </h3>
      {product.coverageNote ? <p className="product-files__note">{product.coverageNote}</p> : null}
      {product.contents && product.contents !== product.coverageNote ? (
        <p className="product-files__note product-files__note--soft">{product.contents}</p>
      ) : null}
      {product.files.length > 0 ? (
        <ul className="product-files__list" aria-label="Dosyalar">
          {product.files.map((file) => (
            <li key={file.name} className="product-file">
              <div className="product-file__head">
                <span className="product-file__name">{file.name}</span>
                <span className="product-file__size mono">
                  {file.format ?? ''}
                  {file.format && file.bytes !== null ? ' · ' : ''}
                  {file.bytes !== null ? formatBytes(file.bytes) : ''}
                </span>
              </div>
              {file.cabinInterior !== null ||
              file.coverage.length > 0 ||
              file.extentMm !== null ||
              file.triangles !== null ? (
                <div className="product-file__tags">
                  {file.cabinInterior !== null ? (
                    <span className={`product-tag product-tag--cabin-${file.cabinInterior}`}>
                      {cabinInteriorLabel(file.cabinInterior)}
                    </span>
                  ) : null}
                  {file.coverage.map((word) => (
                    <span key={word} className="product-tag">
                      {fileCoverageLabel(word)}
                    </span>
                  ))}
                  {file.extentMm !== null ? (
                    <span className="product-tag mono">{formatExtent(file.extentMm)}</span>
                  ) : null}
                  {file.triangles !== null ? (
                    <span className="product-tag mono">{formatNumber(file.triangles)} üçgen</span>
                  ) : null}
                </div>
              ) : null}
              {file.note ? <p className="product-file__note">{file.note}</p> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function ProductDetail({ product, onBack }: { product: ProductCard; onBack: () => void }) {
  const titleId = useId();
  const vehicle = productVehicleLabel(product.vehicle);
  return (
    <article className="product-detail" aria-labelledby={titleId}>
      <button type="button" className="back-link product-detail__back" onClick={onBack} aria-label="Ürün listesine dön">
        <Icon name="chevron-left" size={18} />
        Ürünler
      </button>
      {vehicle ? <p className="product-detail__vehicle">{vehicle}</p> : null}
      <h2 id={titleId} className="product-detail__title">
        {product.title}
      </h2>
      <p className="product-detail__line">
        <span className={`product-status product-status--${product.status}`}>{productStatusLabel(product.status)}</span>
        {' · '}
        <span className="mono">{product.sku}</span>
        {product.listing?.url ? (
          <>
            {' · '}
            <a href={product.listing.url} target="_blank" rel="noreferrer" className="product-detail__listing">
              Etsy ilanı
              <Icon name="external-link" size={12} />
            </a>
          </>
        ) : product.status === 'active' ? (
          <span className="product-detail__nolisting"> · ilanı yok</span>
        ) : null}
      </p>
      <Gallery product={product} />
      <Facts product={product} />
      <Files product={product} />
      {product.listing?.description ? (
        <details className="product-desc">
          <summary className="product-detail__h3 product-desc__summary">Etsy açıklaması</summary>
          <p className="product-desc__text">{product.listing.description}</p>
        </details>
      ) : null}
    </article>
  );
}

export function ProductsPage() {
  const { state, reload } = useProductsView();
  const [filter, setFilter] = useState<ProductFilter>('aktif');
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const searchId = useId();
  if (state.view === null) return <ProductsStatus state={state} reload={reload} />;
  const view = state.view;
  if (view.products.length === 0) {
    return <EmptyState title="Henüz ürün kartı yok" text="Katalog içe alınınca ürünler burada listelenir." />;
  }
  const open = selected === null ? null : (view.products.find((product) => product.sku === selected) ?? null);
  if (open !== null) return <ProductDetail product={open} onBack={() => setSelected(null)} />;
  const needle = query.trim().toLocaleLowerCase('tr-TR');
  const shown = view.products.filter(
    (product) =>
      (filter === 'tumu' || (filter === 'aktif' ? product.status === 'active' : product.status === 'draft')) &&
      (needle === '' || productSearchText(product).includes(needle)),
  );
  return (
    <>
      <p className="shop-summary">
        <span className="mono">{view.counts.active}</span> aktif · <span className="mono">{view.counts.draft}</span>{' '}
        taslak · <span className="mono">{view.counts.total}</span> toplam ·{' '}
        <span className="mono">{view.counts.withImages}</span> üründe <span className="mono">{view.counts.images}</span>{' '}
        görsel
      </p>
      <div className="shop-filters">
        <Segmented<ProductFilter>
          label="Ürün süzgeci"
          controls="urun-liste"
          value={filter}
          onChange={setFilter}
          options={[
            { id: 'aktif', label: 'Aktif', count: view.counts.active },
            { id: 'taslak', label: 'Taslak', count: view.counts.draft },
            { id: 'tumu', label: 'Tümü', count: view.counts.total },
          ]}
        />
        <label className="shop-search">
          <span className="sr-only" id={searchId}>
            Ürün ara
          </span>
          <input
            type="search"
            aria-labelledby={searchId}
            placeholder="Araç, SKU, biçim"
            value={query}
            onChange={(event) => setQuery(event.currentTarget.value)}
          />
        </label>
      </div>
      <div id="urun-liste" role="tabpanel" aria-label="Ürün listesi">
        <ul className="products-grid" aria-label="Ürünler">
          {shown.map((product) => (
            <ProductCardItem key={product.sku} product={product} onOpen={() => setSelected(product.sku)} />
          ))}
        </ul>
        {shown.length === 0 ? <p className="shop-hint">Bu süzgeçle ürün yok.</p> : null}
      </div>
    </>
  );
}
